import React, { useState } from "react";
import GlassCard from "../../components/GlassCard";
import "../styles/NotificationBlock.css";
import { useCurrentLang } from "../../hooks/useCurrentLang";
import { exeNotiText } from "../data/platformText";

const NotificationBlock = ({
  notifications = [],
  onDelete,
  onNotificationClick,
  l_m = "65px",
}) => {
  const [selectMode, setSelectMode] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const currentLang = useCurrentLang();
  const t = exeNotiText[currentLang];

  const toggleSelectMode = () => {
    setSelectMode((prev) => !prev);
    setSelectedIds(new Set());
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === notifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notifications.map((n) => n.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);

    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }

    setSelectedIds(newSelected);
  };

  const deleteSelected = async () => {
    if (!onDelete) return;

    const idsToDelete = Array.from(selectedIds);

    for (const id of idsToDelete) {
      await onDelete(id);
    }

    setSelectedIds(new Set());
    setSelectMode(false);
  };

  const handleNotificationClick = async (notif) => {
    if (selectMode) return;

    const isExpanding = expandedId !== notif.id;
    setExpandedId(isExpanding ? notif.id : null);

    if (isExpanding && !notif.is_read && onNotificationClick) {
      try {
        await onNotificationClick(notif.id);
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="notification-container" style={{ marginLeft: l_m }}>
      {notifications.length > 0 && (
        <div className="select-controls">
          <button className="control-btn" onClick={toggleSelectMode}>
            {selectMode ? t.btn_cancel : t.btn_select}
          </button>

          {selectMode && (
            <>
              <button className="control-btn" onClick={toggleSelectAll}>
                {selectedIds.size === notifications.length
                  ? t.btn_diselectAll
                  : t.btn_selectAll}
              </button>

              <button
                className="control-btn delete-btn"
                onClick={deleteSelected}
                disabled={selectedIds.size === 0}
              >
                {t.delet} ({selectedIds.size})
              </button>
            </>
          )}
        </div>
      )}

      {notifications.map((notif) => {
        const isExpanded = expandedId === notif.id;
        const isSelected = selectedIds.has(notif.id);
        const isUnread = !notif.is_read;

        return (
          <GlassCard
            key={notif.id}
            className={`notification-item ${
              isSelected ? "selected" : ""
            } ${isUnread ? "unread" : "read"}`}
            style={{
              maxHeight: isExpanded ? "775px" : "70px",
              minHeight: "40px",
              transition: "max-height 0.2s ease",
              maxWidth: "775px",
              marginTop: "10px",
              cursor: selectMode ? "default" : "pointer",
              opacity: notif.is_read ? 0.88 : 1,
            }}
            onClick={() => handleNotificationClick(notif)}
          >
            <div className="description-menu">
              <span className="title">
                {notif.title}
                {isUnread && <span className="unread-dot"> •</span>}
              </span>

              <span className="subject">{notif.subject}</span>
              <span className="date">{formatDate(notif.date)}</span>

              {selectMode && (
                <button
                  className="select-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSelect(notif.id);
                  }}
                >
                  {isSelected ? "✓" : ""}
                </button>
              )}
            </div>

            <div className={`content ${isExpanded ? "expanded" : ""}`}>
              <div className="text-preview">{notif.text}</div>
            </div>
          </GlassCard>
        );
      })}
    </div>
  );
};

export default NotificationBlock;